"use client"

import { useMemo } from "react"
import { cn } from "@/lib/utils"

interface DonutChartProps {
  data: {
    label: string
    value: number
    color: string
  }[]
  size?: "sm" | "md" | "lg"
  className?: string
  showTotal?: boolean
  totalLabel?: string
}

export function DonutChart({ 
  data, 
  size = "md", 
  className,
  showTotal = true,
  totalLabel = "รวม"
}: DonutChartProps) {
  const sizeMap = {
    sm: { width: 160, height: 160, strokeWidth: 20, radius: 60 },
    md: { width: 240, height: 240, strokeWidth: 30, radius: 90 },
    lg: { width: 320, height: 320, strokeWidth: 40, radius: 120 },
  }

  const { width, height, strokeWidth, radius } = sizeMap[size]
  const circumference = 2 * Math.PI * radius
  
  // Calculate total and percentages
  const total = useMemo(() => 
    data.reduce((sum, item) => sum + item.value, 0), 
    [data]
  )

  // Filter out zero values and calculate segments
  const segments = useMemo(() => {
    const validData = data.filter(item => item.value > 0)
    let currentOffset = 0
    
    return validData.map((item) => {
      const percentage = (item.value / total) * 100
      const dashLength = (percentage / 100) * circumference
      const dashOffset = currentOffset
      
      currentOffset += dashLength
      
      return {
        ...item,
        percentage,
        dashLength,
        dashOffset,
        rotation: (dashOffset / circumference) * 360 - 90
      }
    })
  }, [data, total, circumference])

  if (total === 0) {
    return (
      <div className={cn("flex items-center justify-center", className)}>
        <div className={cn(
          "rounded-full border-8 border-gray-200 flex items-center justify-center",
          size === "sm" && "w-32 h-32",
          size === "md" && "w-48 h-48",
          size === "lg" && "w-64 h-64"
        )}>
          <span className="text-gray-400 text-lg">ไม่มีข้อมูล</span>
        </div>
      </div>
    )
  }

  return (
    <div className={cn("flex items-center justify-center", className)}>
      <svg width={width} height={height} viewBox={`0 0 ${width} ${height}`}>
        {/* Background circle */}
        <circle
          cx={width / 2}
          cy={height / 2}
          r={radius}
          fill="none"
          stroke="#f3f4f6"
          strokeWidth={strokeWidth}
        />
        
        {/* Data segments */}
        {segments.map((segment, index) => {
          return (
            <g key={index}>
              <circle
                cx={width / 2}
                cy={height / 2}
                r={radius}
                fill="none"
                stroke={segment.color}
                strokeWidth={strokeWidth}
                strokeDasharray={`${segment.dashLength} ${circumference - segment.dashLength}`}
                strokeDashoffset={-segment.dashOffset}
                transform={`rotate(0 ${width / 2} ${height / 2})`}
                style={{
                  transition: 'stroke-dasharray 0.3s ease',
                }}
              />
            </g>
          )
        })}
        
        {/* Center text */}
        {showTotal && (
          <g>
            <text
              x={width / 2}
              y={height / 2 - 10}
              textAnchor="middle"
              className="text-sm fill-gray-600 font-medium"
            >
              {totalLabel}
            </text>
            <text
              x={width / 2}
              y={height / 2 + 20}
              textAnchor="middle"
              className={cn(
                "fill-gray-900 font-bold",
                size === "sm" && "text-xl",
                size === "md" && "text-2xl",
                size === "lg" && "text-3xl"
              )}
            >
              {total.toLocaleString()}
            </text>
          </g>
        )}
      </svg>
    </div>
  )
}
